// #region Dependencias
import styled from 'styled-components'
import { Container, Text } from 'components/Library'
import { colors } from 'components/Library/theme'
import InputImageIcon from './InputImageIcon'
// #endregion

// #region Estilos
const StyledSection = styled.section`
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
  position: relative;
`

const StyledDivContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 0 1rem;
  @media (min-width: 880px) {
    padding: 0 2rem;
  }
`

const StyledDivCard = styled.div.attrs((props) => ({
  style: {
    border: `3px solid ${
      props.color === 'primary'
        ? colors.primary
        : props.color === 'secondary'
        ? colors.secondary
        : props.color === 'tertiary'
        ? colors.tertiary
        : props.color === 'dark'
        ? colors.text
        : props.color
    }`,
    borderRadius: `${props.borderradius}rem`
  }
}))`
  align-items: center;
  border-radius: ${({ borderradius = 1 }) => borderradius}rem;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  display: flex;
  height: 140px;
  margin: 1rem 0;
  width: 100%;

  @media (min-width: 300px) {
    height: 120px;
  }

  @media (min-width: 400px) {
    height: 100px;
  }
  
  @media (min-width: 880px) {
    margin: 1rem;
    place-self: ${({ index }) => (index % 2 !== 0 ? 'flex-end' : 'flex-start')};
    width: calc(50% - 4rem);
  }
`
const StyledSpan = styled.span.attrs((props) => ({
  style: {
    backgroundColor:
      props.color === 'primary'
        ? colors.primary
        : props.color === 'secondary'
          ? colors.secondary
          : props.color === 'tertiary'
            ? colors.tertiary
            : props.color === 'dark'
              ? colors.text
              : props.color
  }
}))`
  align-items: center;
  border-bottom-left-radius: inherit;
  border-top-left-radius: inherit;
  color: ${colors.white};
  display: flex;
  font-size: 3rem;
  font-weight: bold;
  height: 100%;
  justify-content: center;
  text-align: center;
  width: 60px;
  @media (min-width: 420px) {
    width: 80px;
  }
`

const StyledDiv = styled.div`
  display: flex;
  flex-basis: 80%;
  flex-direction: column;
  padding: 0 1rem;
`

const StyledLine = styled.span.attrs((props) => ({
  style: {
    border: `2px solid ${
      props.color === 'primary'
        ? colors.primary
        : props.color === 'secondary'
        ? colors.secondary
        : props.color === 'tertiary'
        ? colors.tertiary
        : props.color === 'dark'
        ? colors.text
        : props.color
    }`
  }
}))`
  border-radius: 1rem;
  bottom: 0;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  display: none;
  height: 100%;
  left: 50%;
  position: absolute;
  top: 0;
  transform: translateX(-50%);

  @media (min-width: 880px) {
    display: unset;
  }
`

const StyledImgContainer = styled.div`
  align-items: center;
  display: flex;
  height: 64px;
  justify-content: center;
  width: 64px;
`

const StyledLineCircle = styled.span.attrs((props) => ({
  style: {
    backgroundColor:
      props.color === 'primary'
        ? colors.primary
        : props.color === 'secondary'
          ? colors.secondary
          : props.color === 'tertiary'
            ? colors.tertiary
            : props.color === 'dark'
              ? colors.text
              : props.color,
    borderRadius: `${props.borderradius}rem`
  }
}))`
  width: 20px;
  height: 20px;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: calc(
    (100% / (${({ totalCircles }) => totalCircles} + 1)) * (${({ index }) => index + 1}) -
      (20px / 2)
  );
`
// #endregion

/**
 * Componente para renderizar una linea de tiempo.
 * @class
 * @param {Object} props - Propiedades del componente.
 * @param {string} props.borderradius - Radio de las esquinas de los elementos del componente.
 * @param {Object} props.colores - Objeto de colores para los elementos del componente.
 * @param {Object} props.valores - Objetos con la información de los elementos de la línea de tiempo.
 * @param {boolean} props.contentEditable - Valor que indica si los elementos son editables.
 * @param {Function} props.handleInputChange - Callback para actualizar los valores de los elementos del componente.
 */
const TimeLine = ({
  idioma,
  borderradius,
  colores,
  valores,
  contentEditable,
  handleInputChange
}) => {
  return (
    <Container
      id="timeline"
      style={{
        backgroundColor: colors.white,
        borderBottom: '1px solid rgba(0, 0, 0, 0.125)',
        padding: '2rem 0'
      }}
    >
      <Text
        align="center"
        color={colores.primario}
        weight="bold"
        contentEditable={contentEditable}
        suppressContentEditableWarning={true}
        data-id={`secciones.timeline.${idioma}.titulo`}
        onBlur={handleInputChange}
        style={{
          fontSize: '2rem',
          marginBottom: '2rem'
        }}
      >
        {valores[idioma].titulo}
      </Text>
      <StyledSection>
        <StyledDivContainer>
          <StyledLine color={colores.primario}>
            {valores.items.map((_, index) => (
              <StyledLineCircle
                key={index}
                borderradius={borderradius}
                color={colores.primario}
                index={index}
                totalCircles={valores.items.length}
              />
            ))}
          </StyledLine>
          {valores.items.map((item, index) => (
            <StyledDivCard
              key={index}
              index={index}
              borderradius={borderradius}
              color={colores.primario}
            >
              <StyledSpan color={colores.primario}>
                <StyledImgContainer>
                  <InputImageIcon
                    name={`secciones.timeline.items.${index}.logo`}
                    modoEdicion={contentEditable}
                    onChange={handleInputChange}
                    alt={index + 1}
                    src={item.logo}
                  />
                </StyledImgContainer>
              </StyledSpan>
              <StyledDiv>
                <Text
                  align="left"
                  color={colores.primario}
                  size="large"
                  weight="bold"
                  contentEditable={contentEditable}
                  suppressContentEditableWarning={true}
                  data-id={`secciones.timeline.items.${index}.${idioma}.titulo`}
                  onBlur={handleInputChange}
                >
                  {item[idioma].titulo}
                </Text>
                <Text
                  align="left"
                  contentEditable={contentEditable}
                  suppressContentEditableWarning={true}
                  data-id={`secciones.timeline.items.${index}.${idioma}.descripcion`}
                  onBlur={handleInputChange}
                >
                  {item[idioma].descripcion}
                </Text>
              </StyledDiv>
            </StyledDivCard>
          ))}
        </StyledDivContainer>
      </StyledSection>
    </Container>
  )
}

export default TimeLine
